<template>
  <v-card>
    <v-card-title>
      {{ I18NGetter().useDealBadge.EDIT_DEAL }}
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="pt-2">
      <v-form :ref="EDIT_DEAL_FORM_KEY">
        <v-container pa-0 grid-list-lg>
          <v-layout wrap>
            <fp-input
              prepend-icon="edit"
              xs12
              v-model.trim="deal.name"
              required
              :minLength="3"
              :label="I18NGetter().useEditDeal.DEAL_NAME"
            />
            <fp-input
              xs12
              prepend-icon="none"
              hide-details
              md9
              :suffix="$env.currentCurrency()"
              type="number"
              :label="
                deal.isCreditDeal
                  ? I18NGetter().useEditDeal.APPLIED_CREDIT_AMOUNT
                  : I18NGetter().useEditDeal.TRANSACTION_VALUE
              "
              v-model="deal.loanAmount"
            />
            <template
              v-if="
                $user.isAdminOrVerifier ||
                $user.privileges.hasAccessToSettlementView
              "
            >
              <fp-input
                v-if="settledBankApplicationNumber !== undefined"
                xs12
                prepend-icon="mdi-file-document"
                md9
                :label="I18NGetter().useReportForm.APPLICATION_NUMBER"
                v-model="deal.settledBankApplicationNumber"
                @input="convertToUpperCase('settledBankApplicationNumber')"
                :type="getApplicationNumberInputType()"
                :mask="applicationNumberMask"
                :custom-rules="getApplicationNumberRules()"
                :placeholder="applicationNumberPlaceholder"
                :description="
                  typeof applicationNumberMaskObject === 'object'
                    ? applicationNumberMaskObject.description
                    : getApplicationNumberDescription() || undefined
                "
                :preserve-mask="true"
                :required="isRequiredBankApplicationNumber()"
              />
              <fp-input
                v-if="settledBankContractNumber !== undefined"
                xs12
                prepend-icon="mdi-file-document-edit"
                md9
                :label="I18NGetter().useReportForm.CONTRACT_NUMBER"
                v-model="deal.settledBankContractNumber"
                @input="convertToUpperCase('settledBankContractNumber')"
                :mask="applicationNumberMask"
                :placeholder="applicationNumberPlaceholder"
                :description="
                  typeof applicationNumberMaskObject === 'object'
                    ? applicationNumberMaskObject.description
                    : undefined
                "
                :custom-rules="[validateContractNumber]"
                :preserve-mask="true"
                required
              />
            </template>
            <v-flex>
              <v-row no-gutters>
                <v-col class="shrink align-self-center">
                  <v-icon class="mr-2">mdi-calendar</v-icon>
                </v-col>
                <fp-month-picker
                  class="pa-0"
                  block
                  v-model="deal.expectedRealisationMonth"
                >
                  <template #label>
                    {{ I18NGetter().useEditDeal.PLANNED_MONTH }}
                    {{
                      deal.isCreditDeal
                        ? I18NGetter().useEditDeal.START
                        : I18NGetter().useEditDeal.PRE_START
                    }}:
                    {{ capitalize(monthYearFormat(deal.expectedRealisationAt)) }}
                  </template>
                </fp-month-picker>
              </v-row>
            </v-flex>
            <fp-datepicker
              xs12
              md9
              prepend-icon="none"
              hide-details
              placeholder="##-##-####"
              :label="
                deal.isCreditDeal
                  ? I18NGetter().useEditDeal.APPLYING_DATE
                  : I18NGetter().useEditDeal.CONTRACT_SIGN_DATE
              "
              v-model="deal.submittedAtDateObject"
            />
            <fp-datepicker
              v-if="ableToEditRealisationDate"
              xs12
              md9
              prepend-icon="none"
              hide-details
              placeholder="##-##-####"
              :label="I18NGetter().useEditDeal.START_DATE"
              :min-date="deal.submittedAtDateObject"
              v-model="deal.realisationAtDateObject"
            />
            <fp-datepicker
              xs12
              md9
              :maxDate="new Date()"
              prepend-icon="none"
              hide-details
              :label="I18NGetter().useEditDeal.LAST_MEETING_CLIENT"
              v-model="deal.lastContactWithClientAtDateObject"
            />
            <v-flex xs12>
              <deal-from-partner-autocomplete
                v-model="deal.partner"
                :disabled="!canEditPartner"
                :return-object="true"
                @input="resetPartner"
                prepend-icon="mdi-handshake"
              />
            </v-flex>
            <template v-if="deal.partner">
              <fp-select
                prepend-icon="mdi-briefcase"
                v-if="sourceListForPartner && sourceListForPartner.length > 0"
                v-model="deal.partnerSource"
                :items="sourceListForPartner"
                item-key="id"
                :return-object="true"
                xs12
                item-name="name"
                :label="sourceLabel"
                :disabled="!canEditSource"
              />
              <fp-select
                prepend-icon="person"
                v-if="
                  salesmanListForPartner && salesmanListForPartner.length > 0
                "
                v-model="deal.partnerSalesman"
                :return-object="true"
                :items="salesmanListForPartner"
                item-key="id"
                xs12
                item-name="displayName"
                :label="I18NGetter().useEditDeal.SALESMAN"
              />
            </template>
            <div class="ml-[33px]" v-if="bankNeedClientAgreedCreditDraft">
              <fp-radio-group
                label="Czy klient wyraził zgodę na przekazanie mu wzoru umowy kredytowej?"
                v-model="deal.clientAgreedCreditDraft"
              />
              <fp-radio-group
                :label="`Czy klient wyraził zgodę na przekazanie mu decyzji kredytowej przed upływem 21 dni od dnia złożenia kompletnego wniosku w banku?`"
                v-model="deal.clientAgreedCreditBefore21Days"
              />
            </div>
            <fp-radio-group
              v-if="deal.canChangeIsLookingForMortgage"
              class="ml-8 mt-4"
              v-model="deal.isLookingForMortgage"
              :label="I18NGetter().useEditDeal.IS_LOOKING_FOR_MORTGAGE"
            />
          </v-layout>
        </v-container>
      </v-form>
    </v-card-text>
    <v-divider />
    <v-card-actions>
      <loading-button
        primary
        :click="() => updateDeal()"
      >
        {{ I18NGetter().useEditDeal.SAVE }}
      </loading-button>
      <v-spacer />
      <loading-button @click="closeModal()">
        {{ I18NGetter().useEditDeal.CANCEL }}
      </loading-button>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { Component } from "vue-property-decorator";
import Deal from "@/models/Deal";
import PlaceholderButton from "@/components/PlaceholderButton.vue";
import { VueModalComponent } from "@/components/fpComponents/services/ModalService/VueModalComponent";
import DealsApi from "@/modules/deals/services/DealsApi";
import Partner, { FinpackSource, Salesman } from "@/models/Partner";
import DictionaryStore from "@/commons/services/store/DictionaryStore";
import { PartnerType } from "@/modules/partner/enums/PartnerType";
import DealFromPartnerAutocomplete from "@/components/inputs/DealFromPartnerAutocomplete.vue";
import { cloneDeep } from "lodash-es";
import { I18NGetter } from "@/services/enumTranslator/I18NGetter";
import { I18NInterface } from "@/services/enumTranslator/I18NInterface";
import { DealStage } from "@/modules/deals/enums/DealStage";
import FpMonthPicker from "@/components/fpComponents/FpMonthPicker.vue";
import { BankSymbol } from "@/commons/dictionaries/BankSymbol";
import { ProductType } from "@/commons/enums/ProductType";
import { AppDomain } from "@/env/enums/Domains";
import { i18nFormatter } from "@/services/StringFormatter";
import { capitalize, monthYearFormat } from '@/composables/useFilters';

@Component({
  components: { DealFromPartnerAutocomplete, PlaceholderButton, FpMonthPicker },
})
export default class EditDeal extends VueModalComponent<Deal> {
  public capitalize = capitalize;
  public monthYearFormat = monthYearFormat;

  public deal: Deal = this.modalData.deal;
  public readonly EDIT_DEAL_FORM_KEY: string = "EDIT_DEAL_FORM_KEY";
  public partnersList: Array<Partner> = [];
  public I18NGetter: () => I18NInterface = I18NGetter;
  public isPartnerAlreadySet: boolean = false;
  public isSourceAlreadySetOrHidden: boolean = false;

  public settledBankApplicationNumber: string | undefined = this.deal.settledBankApplicationNumber;
  public settledBankContractNumber: string | undefined = this.deal.settledBankContractNumber;

  public ProductType = ProductType;

  async mounted() {
    this.isPartnerAlreadySet = !!this.deal.partner;
    this.isSourceAlreadySetOrHidden =
      !!this.deal.partnerSource ||
      !!this.deal.partner?.hidePartnerSensitiveInfo;
    await this.getPartners();
  }

  public async updateDeal(): Promise<void> {
    if (this.$validate(this.EDIT_DEAL_FORM_KEY)) {
      try {
        if (this.deal.partnerSource) {
          this.deal.partnerSourceId = this.deal.partnerSource.id;
        }
        if (this.deal.partnerSalesman) {
          this.deal.partnerSalesmanId = this.deal.partnerSalesman.id;
        }
        const data: Partial<Deal> = cloneDeep(this.deal);
        delete data.status;
        const editedDeal: Deal = await DealsApi.updateDeal(data);
        this.$snackbarService.openSuccessSnackbar(
          I18NGetter().useEditDeal.UPDATE_DEAL_SUCCESS
        );
        this.closeModal(editedDeal);
      } catch (e) {
        this.errorHandler(e, I18NGetter().useEditDeal.UPDATE_DEAL_ERROR);
      }
    }
  }

  public get ableToEditRealisationDate(): boolean {
    return this.$user.isAdminOrVerifier && this.deal.stage === DealStage.WON;
  }

  public get bankNeedClientAgreedCreditDraft(): boolean {
    return this.deal.isMortgage && this.$env.isAppDomain().LENDI_PL;
  }

  public async getPartners() {
    this.partnersList = await DictionaryStore.getDictionary(
      "partnerDictionary"
    );
  }

  public get sourceListForPartner(): Nullable<Array<FinpackSource>> {
    return this.deal.partner?.id &&
      this.partnersList.find((x) => x.id === this.deal.partner?.id)
      ? this.partnersList.find((x) => x.id === this.deal.partner?.id)!.sources
      : null;
  }

  public get salesmanListForPartner(): Nullable<Array<Salesman>> {
    return this.deal.partner?.id &&
      this.partnersList.find((x) => x.id === this.deal.partner?.id)
      ? this.partnersList.find((x) => x.id === this.deal.partner?.id)!.salesman
      : null;
  }

  public get sourceLabel(): string {
    const chosenPartner = this.partnersList.find(
      (x) => x.id === this.deal.partnerId
    );
    return chosenPartner!.type === PartnerType.INTERNET_WEBSITE
      ? I18NGetter().useEditDeal.WHAT_PAGE
      : I18NGetter().useEditDeal.WHAT_INVESTMENT;
  }

  public resetPartner() {
    this.deal.partnerId = this.deal.partner ? this.deal.partner.id : undefined;
  }

  public get canEditPartner(): boolean {
    return (
      this.$user.isAdmin || this.$user.isVerifier || !this.isPartnerAlreadySet
    );
  }

  public get canEditSource(): boolean {
    return (
      this.$user.isAdmin ||
      this.$user.isVerifier ||
      !this.isSourceAlreadySetOrHidden
    );
  }

  get applicationNumberPlaceholder(): string | undefined {
    return this.applicationNumberMask
      ?.replaceAll("#", "1")
      .replaceAll("@", "A");
  }

  public getApplicationNumberDescription = () => {
    if (!this.ApplicationNumberConfig?.description) return;
    return this.ApplicationNumberConfig.description;
  };

  get applicationNumberMask(): string | undefined {
    return typeof this.applicationNumberMaskObject === "string"
      ? this.applicationNumberMaskObject
      : this.applicationNumberMaskObject?.mask;
  }

  get applicationNumberMaskObject():
    | { mask: string; description: string }
    | string
    | undefined {
    const bankSymbol = this.deal.bank?.symbol;
    if (bankSymbol) {
      const productType = this.deal.productType;
      const applicationNumberMask = this.applicationNumberMasks[bankSymbol];
      return productType && applicationNumberMask
        ? applicationNumberMask[productType]
        : undefined;
    } else return undefined;
  }

  public convertToUpperCase(value) {
    this.deal[value] = this.deal[value]?.toUpperCase();
  }

  public applicationNumberMasks: PartialRecord<
    BankSymbol | string,
    PartialRecord<ProductType, { mask: string; description: string } | string>
  > = {
    SANTANDER: {
      [ProductType.MORTGAGE]: "E#######",
      [ProductType.CASH]: "A#######",
    },
    [BankSymbol.ERSTE]: {
      [ProductType.MORTGAGE]: "E#######",
      [ProductType.CASH]: "A#######",
    },
    [BankSymbol.MBANK]: {
      [ProductType.MORTGAGE]: "KHB#########",
      [ProductType.CASH]: "KGB#########",
      [ProductType.SME]: "@@B#########",
    },
    [BankSymbol.MILLENNIUM]: {
      [ProductType.MORTGAGE]: "####/##/####",
    },
    [BankSymbol.BNP]: {
      [ProductType.MORTGAGE]: "H##R##-########",
      [ProductType.CASH]: {
        mask: "@##R##-########",
        description: "np. G12R23-12345678, K83R23-12345678",
      },
    },
    [BankSymbol.ALIOR]: {
      [ProductType.MORTGAGE]: "@*00##########",
      [ProductType.CASH]: "BLP##########",
    },
    [BankSymbol.ING]: {
      [ProductType.MORTGAGE]: "###############",
      [ProductType.CASH]: "###############",
    },
  };

  public checkIfNumber(v: string) {
    return v.match(/[0-9]/);
  }

  public commonRules = {
    textNumber: (min: number, max: number) => {
      return [
        (v: string) => !!v || I18NGetter().VALIDATION.NEW_TRANSLATION_REQUIRED,
        (v: string) =>
          v?.split("").every((el) => this.checkIfNumber(el)) ||
          I18NGetter().VALIDATION.ONLY_DIGITS,
        (v: string) =>
          v?.split("").filter(this.checkIfNumber).join("").length >= min ||
          i18nFormatter(I18NGetter().VALIDATION.MIN_NUMBER_OF_DIGITS, {
            min_number_of_digits: min,
          }),
        (v: string) =>
          v?.split("").filter(this.checkIfNumber).join("").length <= max ||
          i18nFormatter(I18NGetter().VALIDATION.MAX_NUMBER_OF_DIGITS, {
            max_number_of_digits: max,
          }),
      ];
    },
  };

  public ApplicationNumberRules: Partial<
    Record<
      AppDomain,
      PartialRecord<
        BankSymbol | string,
        PartialRecord<
          ProductType,
          {
            rules: ((v: string) => boolean | string)[];
            description: string;
            inputType?: string;
          }
        >
      >
    >
  > = {
    [AppDomain.NESTO_RO]: {
      ING_RO: {
        [ProductType.MORTGAGE]: {
          rules: this.commonRules.textNumber(6, 15),
          description:
            I18NGetter().useCommons.EXAMPLE_ABBREVIATION + " 123456789012345",
          inputType: "text",
        },
        [ProductType.CASH]: {
          rules: this.commonRules.textNumber(6, 15),
          description:
            I18NGetter().useCommons.EXAMPLE_ABBREVIATION + " 123456789012345",
          inputType: "text",
        },
      },
      UCFIN_RO: {
        [ProductType.CASH]: {
          rules: this.commonRules.textNumber(6, 15),
          description:
            I18NGetter().useCommons.EXAMPLE_ABBREVIATION + " 123456789012345",
          inputType: "text",
        },
      },
    },
  };

  get ApplicationNumberConfig():
    | {
        rules: ((v: string) => boolean | string)[];
        description: string;
        inputType?: string;
      }
    | undefined {
    const bankSymbol = this.deal.bank?.symbol;
    const productType = this.deal.productType;
    if (!bankSymbol || !productType) return undefined;
    return this.ApplicationNumberRules?.[this.$env.appDomain]?.[bankSymbol]?.[
      productType
    ];
  }
  public getApplicationNumberInputType = () => {
    if (!this.ApplicationNumberConfig?.inputType) return "text";
    return this.ApplicationNumberConfig.inputType;
  };

  public getApplicationNumberRules = () => {
    if (!this.ApplicationNumberConfig?.rules?.length) return;
    return this.ApplicationNumberConfig.rules;
  };

  public validateContractNumber = (v: string) => {
    if (this.deal.bank && this.deal.bank.symbol === BankSymbol.ING) {
      return (
        (v && v.length === 15) ||
        "Numer umowy musi zawierać dokładnie 15 znaków"
      );
    }
    return true;
  };

  public isRequiredBankApplicationNumber = () => {
    if (!this.ApplicationNumberConfig?.rules?.length) {
      return this.$env.getAppDomainConfig().moduleSettings.REPORTS
        .hasObligatoryBankApplicationNumber;
    }
    return false;
  };
}
</script>
